import {useEffect, useRef} from 'react';
import {useInView} from '../../../../hooks/useInView/useInView';
import {VideoType} from '../../Video/type';

// The play() Promise can throw on Safari even when there's no visible issue.
// We ignore these errors to prevent unnecessary DataDog noise.
// eslint-disable-next-line no-empty-function
const noop = () => {};

export const useVideoOrFallbackImage = (video?: VideoType) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasLoadedRef = useRef(false);

  const {ref: containerRef, inView} = useInView<HTMLDivElement>({
    threshold: 0.05,
    triggerOnce: false,
    skip: !video,
  });

  // eslint-disable-next-line local-rules/prefer-use-inview-effect
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (!inView) {
      videoRef.current.pause();
      return;
    }

    // Video already loaded - play it since it's in view
    if (hasLoadedRef.current) {
      videoRef.current.play().catch(noop);
      return;
    }

    // iOS Safari ignores preload="auto" to save bandwidth, so we must manually trigger .load()
    // readyState: 0=HAVE_NOTHING, 1=HAVE_METADATA, 2=HAVE_CURRENT_DATA, 3=HAVE_FUTURE_DATA, 4=HAVE_ENOUGH_DATA
    if (videoRef.current.readyState === 0) {
      videoRef.current.load();
    } else {
      // Desktop: video already preloaded while out of view
      hasLoadedRef.current = true;
      videoRef.current.play().catch(noop);
    }
  }, [inView]);

  // Called via onCanPlay - manually trigger play since autoPlay attribute causes laggy videos on iOS
  const handleCanPlay = () => {
    hasLoadedRef.current = true;

    if (inView) {
      videoRef.current?.play().catch(noop);
    }
  };

  return {
    containerRef,
    handleCanPlay,
    videoRef,
  };
};
